import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { FileText, Clock, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { formatAUDate } from '../lib/dateUtils';

const statusColors = {
  pending: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  completed: 'bg-green-100 text-green-800 border-green-200',
  expired: 'bg-muted text-muted-foreground border-border'
};

export default function MyForms() {
  const navigate = useNavigate();
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadAssignments();
  }, []);

  const loadAssignments = async () => {
    try {
      const response = await api.get('/api/forms/assignments');
      setAssignments(response.data);
    } catch (error) {
      toast.error('Failed to load forms');
    } finally {
      setLoading(false);
    }
  };

  const pendingForms = assignments.filter(a => a.status === 'pending');
  const completedForms = assignments.filter(a => a.status === 'completed');

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-muted rounded w-1/4"></div>
          <div className="h-64 bg-muted rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-foreground" style={{fontFamily: 'Plus Jakarta Sans, sans-serif'}}>
          My Forms
        </h1>
        <p className="text-muted-foreground mt-1">Complete forms requested by your accountant</p>
      </div>

      {/* Pending Forms */}
      {pendingForms.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg" style={{fontFamily: 'Plus Jakarta Sans, sans-serif'}}>
              Pending Forms ({pendingForms.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {pendingForms.map((assignment) => (
                <div key={assignment.id} className="flex items-center justify-between p-4 border border-yellow-200 bg-yellow-50 rounded-lg">
                  <div>
                    <h3 className="font-semibold text-foreground">{assignment.template_name}</h3>
                    <p className="text-sm text-muted-foreground mt-1">Assigned {formatAUDate(assignment.assigned_at)}</p>
                    {assignment.due_date && (
                      <p className="text-xs text-muted-foreground mt-1">Due: {formatAUDate(assignment.due_date)}</p>
                    )}
                  </div>
                  <Button 
                    onClick={() => navigate(`/forms/fill/${assignment.id}`)}
                    className="bg-primary hover:bg-primary/90"
                  >
                    Complete Form
                  </Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Completed Forms */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg" style={{fontFamily: 'Plus Jakarta Sans, sans-serif'}}>
            Completed Forms ({completedForms.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {completedForms.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <CheckCircle className="w-12 h-12 mx-auto mb-3 text-muted-foreground/50" />
              <p>No completed forms yet</p>
            </div>
          ) : (
            <div className="space-y-3">
              {completedForms.map((assignment) => (
                <div key={assignment.id} className="flex items-center justify-between p-4 border rounded-lg">
                  <div>
                    <h3 className="font-semibold text-foreground">{assignment.template_name}</h3>
                    <p className="text-sm text-muted-foreground">Completed {formatAUDate(assignment.completed_at)}</p>
                  </div>
                  <Badge className={statusColors.completed}>
                    <CheckCircle className="w-3 h-3 mr-1" />
                    Completed
                  </Badge>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
